import { DescriptionOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import type { GridPaginationModel } from "@mui/x-data-grid";
import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../../../../../components/UIToolkit/molecules/EmptyState";
import { SystemBanner } from "../../../../../components/UIToolkit/molecules/SystemBanner";
import SystemDialog from "../../../../../components/UIToolkit/Popups/Dialog";
import { SystemDataGrid } from "../../../../../components/UIToolkit/Tables/SystemDataGrid";
import type { SystemGridColDef } from "../../../../../components/UIToolkit/Tables/SystemDataGrid/types";
import { useOpenPresignedFile } from "../../../../../hooks/useOpenPresignedFile";
import { backendService } from "../../../../../services/backend.service";
import { INK } from "../../../../../theme/tokens";
import { formatIsoDateTime } from "../../../../../utils/dateUtils";
import type { ExternalSource, HrisIntegrationType } from "../../../../../utils/models";
import { useSftpFilePreview } from "../sftpHooks";
import { SftpDepartmentKeysButton } from "./SftpDepartmentKeysButton";

type Props = {
	open: boolean;
	folder: string;
	file: string;
	/** The feed this file is being checked for — what makes "which column routes?" answerable. */
	source: ExternalSource;
	type: HrisIntegrationType;
	onClose: () => void;
};

/** A preview row keyed by its position in the file — the file itself carries no id. */
type PreviewRow = { rowIndex: number } & Record<string, string | number>;

/** Column field for the nth column of the file. Positional, because a vendor header can repeat. */
const columnField = (index: number): string => `column${index}`;

const MIN_COLUMN_WIDTH = 140;

/**
 * Where the preview starts, and the page sizes it offers. A page size is also the request size, so the
 * whole file is reachable without any single response carrying more than 100 rows of it — the same
 * options the app's other grids use, all inside the endpoint's own per-request cap.
 */
const INITIAL_PAGINATION: GridPaginationModel = { page: 0, pageSize: 50 };
const PAGE_SIZE_OPTIONS = [25, 50, 100];

/**
 * Placeholder columns to shimmer while the response is in flight.
 *
 * The columns ARE part of the payload here, so there is nothing real to skeleton yet — and
 * `SystemDataGrid` builds its skeleton from the column set it is given, so handing it an empty one
 * renders a bare box that reads as "this file has no data" rather than "we are still reading it".
 */
const PLACEHOLDER_COLUMN_COUNT = 5;

/**
 * Read-only preview of a delivered SFTP feed — its real headers and first rows, plus a download.
 *
 * The point is to answer "is this the right file?" before it is wired to a department: the columns
 * are the vendor's own (the backend deliberately does not normalize them), so a header the syncher
 * cannot map is visible here rather than after a failed sync.
 */
export const SftpFilePreviewDialog: FC<Props> = ({ open, folder, file, source, type, onClose }) => {
	const { t } = useTranslation();
	const { openPresignedFile } = useOpenPresignedFile();
	/** Opens the object a configured (folder, file) pair resolves to — including its archived copy. */
	const downloadFile = (folder: string, file: string) =>
		openPresignedFile({
			fetchUrl: () => backendService.getSftpFileUrl(folder, file),
			errorMessage: t("department.config.integrations.sftp.download.error"),
			analyticsEvent: "dept_config_sftp_file_download",
			analyticsProps: { folder, file },
			action: "download sftp file",
		});
	// Which page is being read. A caller that can swap `file` while this stays mounted keys the dialog on
	// it, so page 4 of the last file is never shown as a page of this one.
	const [pagination, setPagination] = useState<GridPaginationModel>(INITIAL_PAGINATION);
	const { data: preview, isFetching, error } = useSftpFilePreview({ folder, file, page: pagination.page, pageSize: pagination.pageSize, enabled: open });

	const columns = useMemo<SystemGridColDef<PreviewRow>[]>(() => {
		if (!preview) {
			// Header text deliberately blank: a placeholder column must not name anything, only hold the
			// shape the skeleton shimmers in.
			return Array.from({ length: PLACEHOLDER_COLUMN_COUNT }, (_, index) => ({
				field: columnField(index),
				headerName: "",
				minWidth: MIN_COLUMN_WIDTH,
				flex: 1,
				sortable: false,
			}));
		}
		return preview.columns.map((header, index) => ({
			field: columnField(index),
			// A blank vendor header is real (a trailing delimiter column) — label it by position
			// rather than rendering a nameless column the operator cannot refer to.
			headerName: header || t("department.config.integrations.sftp.preview.unnamedColumn", { index: index + 1 }),
			minWidth: MIN_COLUMN_WIDTH,
			flex: 1,
			sortable: false,
		}));
	}, [preview, t]);

	// Numbered from the page's own offset, not from 0: `rowIndex` is the grid's row id, and restarting
	// it per page would make row 1 of page 2 collide with row 1 of page 1.
	const rows = useMemo<PreviewRow[]>(
		() =>
			(preview?.rows ?? []).map((values, indexInPage) => ({
				rowIndex: (preview?.row_offset ?? 0) + indexInPage,
				...Object.fromEntries(values.map((value, index) => [columnField(index), value])),
			})),
		[preview?.rows, preview?.row_offset],
	);

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			maxWidth="lg"
			fullWidth
			// A plain string, not a node: SystemDialog interpolates the title into its aria-labelledby id.
			title={t("department.config.integrations.sftp.preview.title", { file })}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
					{/* Answers "the file moved to archived — am I seeing the last one, or nothing?". The
					    resolver falls back to the newest archived copy, so it IS the last delivery; that is
					    the normal state for a healthy feed and worth saying rather than leaving to inference. */}
					{preview?.is_archived_copy && (
						<SystemBanner
							tone="info"
							title={t("department.config.integrations.sftp.preview.archived.title")}
							description={
								preview.last_modified
									? t("department.config.integrations.sftp.preview.archived.description", {
											date: formatIsoDateTime(preview.last_modified),
										})
									: t("department.config.integrations.sftp.preview.archived.descriptionNoDate")
							}
							dataTestId="sftp-preview-archived"
						/>
					)}
					<SystemDataGrid<PreviewRow>
						rows={rows}
						columns={columns}
						getRowId={(row) => row.rowIndex}
						// Only true on the FIRST read of a page we have nothing for: `placeholderData` keeps the
						// previous page rendered while the next one loads, so paging must not blank the grid.
						loading={isFetching && !preview}
						error={!!error}
						errorMessage={t("department.config.integrations.sftp.preview.error")}
						containedHeight={{ minHeight: 200, maxHeight: 420 }}
						emptyState={<EmptyState icon={<DescriptionOutlined />} title={t("department.config.integrations.sftp.preview.empty")} />}
						// The file's codes, not the grid's — the answer an operator needs next, and the reason
						// this sits in the toolbar rather than under the rows.
						trailingToolbarNodes={
							<Box sx={{ ml: "auto" }}>
								<SftpDepartmentKeysButton folder={folder} file={file} source={source} type={type} />
							</Box>
						}
						// Server-side pagination: one page of the file per request, so a 40MB timesheet is
						// reachable row by row without ever being shipped to the browser whole. `initialState` is
						// cleared because MUI warns when a controlled model and an initial one are both given.
						dataGridProps={{
							paginationMode: "server",
							rowCount: preview?.total_rows ?? 0,
							paginationModel: pagination,
							onPaginationModelChange: setPagination,
							pageSizeOptions: PAGE_SIZE_OPTIONS,
							initialState: undefined,
						}}
						dataTestId="sftp-file-preview"
					/>
					{/* The caption always says something while the request is open: silence under a
					    shape-less grid is what made "still loading" look like "nothing here". The row range is
					    the grid footer's job now that every row is reachable — this names the file and its size. */}
					{preview ? (
						<Typography variant="caption" sx={{ color: INK.MUTED }}>
							{t("department.config.integrations.sftp.preview.rowCount", { total: preview.total_rows })}
							{" · "}
							{preview.resolved_file_key}
						</Typography>
					) : (
						isFetching && (
							<Typography variant="caption" role="status" sx={{ color: INK.MUTED }}>
								{t("department.config.integrations.sftp.preview.loading")}
							</Typography>
						)
					)}
				</Box>
			}
			cancel={t("close")}
			confirm={t("department.config.integrations.sftp.download")}
			confirmButtonVariant="primary"
			confirmDisabled={!preview}
			onConfirm={() => void downloadFile(folder, file)}
			dataTestId="sftp-file-preview-dialog"
		/>
	);
};
